import Spinner from "@src/lib/ui/Spinner";
import Text from "@src/lib/ui/Text";
import gql from "graphql-tag";
import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { Switch } from "@src/lib/ui/Switch";
import { Input } from "@src/lib/ui/Input";
import { GraphqlOperations } from "@src/types.generated";
import useUser from "@src/features/app/hooks/useUser";
import { useFeatureFlagsQuery } from "@src/lib/contexts/FeatureFlagContext.generated";
import { Label } from "@src/lib/ui/Label";
import Fuse from "fuse.js";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@src/lib/ui/Accordion";
import { useUpdateEmployeeFeatureFlagsMutation } from "@src/features/app/top-nav/SetEmployeeFeatureFlags.generated";

const flagDisplayName = (f: { name: string; userFacingName?: string | null }) =>
  f.userFacingName || f.name;

const SetEmployeeFeatureFlags: React.FC = () => {
  const { data, loading } = useFeatureFlagsQuery();
  const { isStaff } = useUser();
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [updateEmployeeFeatureFlagsMutation] =
    useUpdateEmployeeFeatureFlagsMutation({
      refetchQueries: [GraphqlOperations.Query.FeatureFlags],
    });

  const updateEmployeeFeatureFlags = useCallback(
    (name: string, enabled: boolean) => {
      updateEmployeeFeatureFlagsMutation({
        variables: {
          input: {
            featureFlags: [
              {
                name,
                enabled: { value: enabled },
              },
            ],
          },
        },
      });
    },
    [updateEmployeeFeatureFlagsMutation],
  );

  const featureFlags = useMemo(() => {
    if (!data?.FeatureFlags) return [];
    return [...data.FeatureFlags].sort((a, b) =>
      flagDisplayName(a).localeCompare(flagDisplayName(b)),
    );
  }, [data?.FeatureFlags]);

  const fuse = useMemo(
    () =>
      new Fuse(featureFlags, {
        keys: ["name", "userFacingName"],
        threshold: 0.3,
      }),
    [featureFlags],
  );

  const filteredFeatureFlags = useMemo(() => {
    if (!searchTerm) return featureFlags;
    return fuse.search(searchTerm).map((result) => result.item);
  }, [fuse, searchTerm, featureFlags]);

  const featureFlagToggleRows = useMemo(() => {
    return filteredFeatureFlags.map((f) => (
      <FeatureFlagToggleRow
        key={f.name}
        title={flagDisplayName(f)}
        selected={f.enabled}
        onUpdate={(checked) => updateEmployeeFeatureFlags(f.name, checked)}
      />
    ));
  }, [filteredFeatureFlags, updateEmployeeFeatureFlags]);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  if (!isStaff) return null;

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="feature-flags">
        <AccordionTrigger>
          <Text variant="body-title">User level feature flags</Text>
        </AccordionTrigger>
        <AccordionContent>
          <Input
            ref={searchInputRef}
            type="text"
            placeholder="Search feature flags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-3"
          />
          {loading ? (
            <Spinner />
          ) : (
            <div className="flex flex-col gap-1.5">{featureFlagToggleRows}</div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

type FeatureFlagToggleRowProps = {
  title: string;
  selected: boolean;
  onUpdate: (checked: boolean) => void;
};

const FeatureFlagToggleRow: React.FC<FeatureFlagToggleRowProps> = (props) => (
  <div className="flex items-center py-0.5">
    <Label htmlFor={props.title} className="flex-1">
      {props.title}
    </Label>
    <Switch
      id={props.title}
      checked={props.selected}
      onCheckedChange={props.onUpdate}
    />
  </div>
);

gql`
  mutation UpdateEmployeeFeatureFlags(
    $input: UpdateEmployeeFeatureFlagsInput!
  ) {
    updateEmployeeFeatureFlags(input: $input) {
      success
    }
  }
`;

export default SetEmployeeFeatureFlags;
